import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Select } from 'antd';
import { useDataProvider, useNotify, useQuery, useQueryWithStore } from 'react-admin';
import { ReloadOutlined } from '@ant-design/icons';
import { ExtendedDataProvider } from '@src/types';
import { Field, FormSection, ToggleField } from '@src/components/Common/Form';
import { spinnerElement } from '@src/components/Common/utils';

const { Option } = Select;

const PACK_RESOURCE = 'serviceowner_pack_profile';

/** which fields belong to Pack rather than to the service itself */
const PACK_FIELDS = [
    'pack_enabled',
    'universal_executor_enabled',
    'auto_trigger_on_change_approval',
    'show_pack_to_consumer',
];

/** the ids behind a team field, whichever shape it is holding at the time */
const teamIds = (value: any): number[] =>
    (value || []).map((entry: any) => (typeof entry === 'object' ? entry.value ?? entry.id : entry));

/**
 * Everything about how a service behaves, in one form behind one button.
 *
 * Settings, allowed teams and dependant teams were three tabs with three save
 * buttons, but they are three parts of one serializer on one endpoint — and
 * that endpoint takes a PUT, so saving any one of them alone sent a payload
 * missing the other two. Pack is a fourth endpoint, but from where the reader
 * sits it is the same question: how should this service behave. So it is the
 * same form and the same button; only the requests underneath differ.
 */
const ServiceSettingsTab = ({ record, canEdit }: { record: any; canEdit: boolean }) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    // the list record carries no teams, so the service is read in full here
    const {
        data: service,
        loaded,
        error: serviceError,
        refetch: refetchService,
    } = useQuery({
        type: 'getOne',
        resource: 'services',
        payload: { id: record.id },
    });

    const { data: versions } = useQuery({
        type: 'getAction',
        resource: 'service_schemas',
        payload: { verb: 'versions', filter: { service_id: record.id } },
    });

    /**
     * Read straight rather than through the store: `useQueryWithStore` reports
     * a failure by leaving `loaded` false with no error to read, which is
     * indistinguishable from still loading — and this tab is gated on it.
     */
    const {
        data: pack,
        loaded: packLoaded,
        error: packError,
        refetch: refetchPack,
    } = useQuery({
        type: 'getAction',
        resource: PACK_RESOURCE,
        payload: { id: undefined, verb: `service/${record.id}/config` },
    });

    /**
     * Every team, once. Both pickers search the same set, so they load it
     * together and filter in the browser — a team list is short, and searching
     * it server-side per keystroke bought nothing but a debounce.
     */
    const { data: teams } = useQueryWithStore({
        type: 'getList',
        resource: 'all_teams',
        payload: {
            pagination: { page: 1, perPage: 500 },
            sort: { field: 'name', order: 'asc' },
            filter: {},
        },
    });

    const teamOptions = useMemo(
        () => ((teams as any[]) || []).map((team) => ({ value: team.id, label: team.name })),
        [teams],
    );

    // the owning team cannot depend on its own service
    const dependantOptions = useMemo(
        () => teamOptions.filter((team) => team.value !== service?.owner?.id),
        [teamOptions, service],
    );

    useEffect(() => {
        if (!service) return;
        form.setFieldsValue({
            approval_required: service.approval_required,
            enabled_notifications: service.enabled_notifications,
            allow_manual_approval: service.allow_manual_approval,
            allow_manual_completion: service.allow_manual_completion,
            minimum_version: service.minimum_version ?? 0,
            allowed_teams: teamIds(service.allowed_teams),
            dependant_teams: teamIds(service.dependant_teams),
        });
    }, [service, form]);

    useEffect(() => {
        if (!packLoaded) return;
        form.setFieldsValue({
            pack_enabled: pack?.pack_enabled || false,
            universal_executor_enabled: pack?.universal_executor_enabled || false,
            auto_trigger_on_change_approval: pack?.auto_trigger_on_change_approval ?? true,
            show_pack_to_consumer: pack?.show_pack_to_consumer ?? false,
        });
    }, [pack, packLoaded, form]);

    /**
     * One button, two endpoints. They are sent together and reported together:
     * a half-saved form would leave the reader unable to tell which half took.
     */
    const save = (values: any) => {
        setSaving(true);

        const packValues: Record<string, any> = {};
        const serviceValues: Record<string, any> = {};
        Object.entries(values).forEach(([key, value]) => {
            (PACK_FIELDS.includes(key) ? packValues : serviceValues)[key] = value;
        });

        return Promise.all([
            dataProvider.update('services', {
                id: record.id,
                // the API takes a PUT, so everything writable travels together
                data: {
                    ...serviceValues,
                    allowed_teams: teamIds(serviceValues.allowed_teams),
                    dependant_teams: teamIds(serviceValues.dependant_teams),
                },
                previousData: { id: record.id },
            }),
            dataProvider.createAction(PACK_RESOURCE, {
                data: { ...(pack || {}), ...packValues },
                verb: `service/${record.id}/`,
            }),
        ])
            .then(() => notify('Service settings have been updated', 'success'))
            .catch((error: any) => {
                notify('Something went wrong', 'warning');
                if (error.status === 400 && error.body) {
                    Object.entries<any>(error.body).forEach(([name, errors]) =>
                        form.setFields([{ name, errors }]),
                    );
                }
            })
            .finally(() => setSaving(false));
    };

    /**
     * A request that failed left this tab at its spinner for ever: both hooks
     * report a failure as "not loaded", so the gate below could not tell the
     * two apart. Say so instead, and offer the only thing that helps.
     */
    if (serviceError || packError) {
        return (
            <div className="svc-failed" role="alert">
                <span>These settings could not be loaded.</span>
                <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                        if (serviceError) refetchService();
                        if (packError) refetchPack();
                    }}
                >
                    Try again
                </Button>
            </div>
        );
    }

    if (!loaded || !packLoaded) return spinnerElement;

    return (
        <div className="nf-form nf-form--sheet svc-settings">
            <Form form={form} onFinish={save} layout="vertical" colon={false} disabled={!canEdit}>
                <FormSection id="service-settings" title="Settings" bare>
                    {/* Who the service is for, first: the two pickers hold lists
                        of names, so they lead the form and take half of it each. */}
                    <Field name="allowed_teams" label="Allowed teams" span={6}>
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Any team"
                            options={teamOptions}
                            maxTagCount="responsive"
                        />
                    </Field>

                    <Field name="dependant_teams" label="Dependant teams" span={6}>
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Select teams"
                            options={dependantOptions}
                            maxTagCount="responsive"
                        />
                    </Field>

                    {/* what the service does with a change, four across. Nothing
                        on this row is labelled, so nothing needs a label slot. */}
                    <ToggleField
                        name="approval_required"
                        span={3}
                        label="Approval required"
                        className="nf-field--unlabelled"
                    />
                    <ToggleField
                        name="enabled_notifications"
                        span={3}
                        label="Email notifications"
                        className="nf-field--unlabelled"
                    />
                    <ToggleField
                        name="allow_manual_approval"
                        span={3}
                        label="Manual approval"
                        className="nf-field--unlabelled"
                    />
                    <ToggleField
                        name="allow_manual_completion"
                        span={3}
                        label="Manual completion"
                        className="nf-field--unlabelled"
                    />

                    {/* Pack is a different endpoint but the same question, and
                        one button sends both — so its switches sit on the last
                        row beside the version they are saved with. */}
                    <Field name="minimum_version" label="Minimum schema version" span={3}>
                        <Select>
                            <Option key={0} value={0}>
                                Latest
                            </Option>
                            {versions &&
                                Object.values(versions).map((entry: any) => (
                                    <Option key={entry.version} value={entry.version}>
                                        {entry.version}
                                    </Option>
                                ))}
                        </Select>
                    </Field>

                    <ToggleField name="pack_enabled" span={3} label="Enable Pack" />
                    <ToggleField name="universal_executor_enabled" span={3} label="Universal Executor" />
                    <ToggleField
                        name="auto_trigger_on_change_approval"
                        span={3}
                        label="Auto-trigger on approval"
                    />
                    <ToggleField
                        name="show_pack_to_consumer"
                        span={3}
                        label="Show Pack to consumers"
                    />

                    {canEdit && (
                        <div className="svc-save">
                            {/* the form is disabled for a reader, and a disabled
                                form disables its own submit — so this opts out */}
                            <Button type="primary" htmlType="submit" loading={saving} disabled={false}>
                                Save changes
                            </Button>
                        </div>
                    )}
                </FormSection>
            </Form>
        </div>
    );
};

export default ServiceSettingsTab;
